import {
  ActionIdeaForm,
  CreateIdeaFormFieldTypes,
  CreateIdeaFormRef,
} from './action-idea-form.tsx'
import { FillStrategies } from './action-idea-form.ts'
import { UserInfo } from '../../../../../apis/auth/auth.def.ts'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectUser } from '../../../../../features/user/user.slice.ts'
import { FormInstance } from 'antd/lib/index'
import notification from 'antd/es/notification'
import IdeyaModal from '../../../../../common/antd/ideya-modal.tsx'
import { IdeyaSpinner } from '../../../../../common/components/ideya-spinner.tsx'
import { useGetBookDetailsQuery } from '../../../../../apis/book/book-api.api.ts'
import { useGetIdeaDetailQuery } from '../../../../../apis/idea/idea-api.api.ts'
import { getErrorDescription } from '../../../../../redux/utils.ts'
import { NEW_RELIC, newRelic } from '@/utils/new-relic.ts'
import { useEffect, useRef } from 'react'
import { LiquidateIdeaForm } from './liquidate-idea-form.tsx'
import { useIsMobile } from '../../../../../common/hooks/use-is-mobile.ts'
import './action-idea-modal.scss'
import { useTranslation } from 'react-i18next'
import { IdeaDirectionType } from '../../../../../enums/idea-list.enum.ts'

interface ActionIdeaModalProps {
  showActionModal: boolean
  showLiquidateModal: boolean
  setShowLiquidatedModal: (v: boolean) => void
  form: FormInstance<CreateIdeaFormFieldTypes>
  handleCloseModal: () => void
  onIdeaActioned: () => void
  editIdeaId?: string | null
  setEditIdeaId: (id: string | null) => void
}

export const ActionIdeaModal: React.FC<ActionIdeaModalProps> = ({
  showActionModal,
  showLiquidateModal,
  setShowLiquidatedModal,
  form,
  handleCloseModal,
  onIdeaActioned,
  editIdeaId,
  setEditIdeaId,
}) => {
  const { t } = useTranslation()
  const userInfo: UserInfo = useAppSelector(selectUser)
  const [notificationApi, notificationContextHolder] =
    notification.useNotification()

  const {
    data,
    isFetching,
    isLoading,
    refetch: refetchGetBookDetailsQuery,
  } = useGetBookDetailsQuery(
    { id: userInfo.bookId },
    { skip: !userInfo.bookId }
  )

  const {
    currentData: editData,
    isFetching: isFetchingEdit,
    isError: isEditError,
    error: editError,
    refetch: refetchGetIdeaDetailQuery,
  } = useGetIdeaDetailQuery(editIdeaId ?? '', {
    skip: !editIdeaId,
  })

  // Edit mode has nothing to render until the detail arrives. Covers the error
  // frame too, where fetching is already false but the modal only closes on the
  // next render — without this the empty form flashes.
  const showSpinner =
    isFetching || isLoading || isFetchingEdit || (!!editIdeaId && !editData)

  useEffect(() => {
    if (!editIdeaId) {
      form.resetFields()
      return
    }

    if (editData?.data) {
      form.resetFields()
      form.setFieldsValue({
        figi: editData.data.ticker?.figi,
        direction: editData.data.direction as IdeaDirectionType,
        targetMarketValue: Math.round(editData.data.currentMarketValue),
        target: editData.data.target,
        stop: editData.data.stop,
        fillStrategy:
          // Previous fill strategy was 15min-VWAP, hardcode it to 1hr-vwap now
          editData.data.fillStrategy === '15min-VWAP' ||
          editData.data.fillStrategy === '1hour-VWAP'
            ? FillStrategies[0].value
            : editData.data.fillStrategy,
        keepFilling: editData.data.keepFilling,
        tradeReason: editData.data.tradeReason,
        horizon: editData.data.horizon,
        conviction: editData.data.conviction,
        comment: editData.data.description,
      })
    }
  }, [editIdeaId, editData, form])

  const createIdeaFormRef = useRef<CreateIdeaFormRef>(null)
  const isMobile = useIsMobile(768)

  const handleCloseAndClear = () => {
    setEditIdeaId(null)
    handleCloseModal()
    form.resetFields()
    createIdeaFormRef.current?.resetFormState()
  }

  // Read through a ref so the error effect below doesn't re-run (and re-notify)
  // when this handler's identity changes.
  const handleCloseAndClearRef = useRef(handleCloseAndClear)
  handleCloseAndClearRef.current = handleCloseAndClear

  useEffect(() => {
    if (!showActionModal || !isEditError) return

    const description = getErrorDescription(editError)
    newRelic.recordCustomEvent('GetIdeaDetailFailed', {
      element: NEW_RELIC.ERROR.ideaDetail,
      event: description,
    })
    notificationApi.error({
      message: 'Error',
      description,
      placement: 'topRight',
    })
    handleCloseAndClearRef.current()
  }, [showActionModal, isEditError, editError, notificationApi])

  return (
    <>
      {notificationContextHolder}
      <IdeyaModal
        open={showActionModal}
        title={
          editIdeaId
            ? t('idea_list.idea_modal.action_idea_modal.edit_idea')
            : t('idea_list.idea_modal.action_idea_modal.create_idea')
        }
        destroyOnClose
        isForm={true}
        width={isMobile ? '98vw' : 1300}
        onCancel={handleCloseAndClear}
        style={isMobile ? { top: 4, padding: 4 } : {}}
        className={'action-idea-modal'}
      >
        {showSpinner ? (
          <div
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <IdeyaSpinner />
          </div>
        ) : (
          <ActionIdeaForm
            key={editIdeaId ?? 'create'}
            ref={createIdeaFormRef}
            form={form}
            onCancel={handleCloseAndClear}
            buyingPower={data?.data?.buyingPower}
            usedBuyingPower={data?.data?.usedBuyingPower}
            userInfo={userInfo}
            onIdeaActioned={() => {
              onIdeaActioned()
              refetchGetBookDetailsQuery()
              if (editIdeaId) {
                refetchGetIdeaDetailQuery()
              }
            }}
            onEditIdea={(ideaId: string) => setEditIdeaId(ideaId)}
            mode={editIdeaId ? 'edit' : 'create'}
            ideaDetail={editData?.data}
            onIdeaLiquidated={() => setShowLiquidatedModal(true)}
          />
        )}
      </IdeyaModal>
      <IdeyaModal
        open={showLiquidateModal}
        onCancel={() => setShowLiquidatedModal(false)}
        title={t('idea_list.idea_modal.action_idea_modal.liquidate_idea')}
        isForm={true}
        width={isMobile ? '95vw' : 500}
        zIndex={1500}
        style={isMobile ? { top: 8, padding: 8 } : {}}
      >
        <LiquidateIdeaForm
          ideaId={editIdeaId ?? ''}
          fillStrategy={
            form.getFieldValue('fillStrategy')
              ? form.getFieldValue('fillStrategy')
              : editData
                ? editData?.data.fillStrategy === '15min-VWAP' ||
                  editData?.data.fillStrategy === '1hour-VWAP'
                  ? FillStrategies[0].value
                  : FillStrategies[1].value
                : FillStrategies[0].value
          }
          onIdeaActioned={onIdeaActioned}
          onCancel={() => setShowLiquidatedModal(false)}
          onSuccess={() => {
            setShowLiquidatedModal(false)
            handleCloseAndClear()
          }}
        />
      </IdeyaModal>
    </>
  )
}
